import React from 'react';
import { AssessmentSectionProps } from '../../../types/health-tools';
import HealthCard from '../ui/HealthCard';
import SkeletonHealthCard from '../ui/SkeletonHealthCard';
import { Scale, Flame, Heart, Calculator, Award } from 'lucide-react';

/**
 * Assessment Section Component
 * 
 * Displays health assessment tools including:
 * - Health calculators for BMI, calorie needs, etc.
 * - Risk assessment tools
 * - Health screening tools
 * 
 * @param calculators - Array of calculator tools
 * @param riskAssessments - Array of risk assessment tools
 * @param screeningTools - Array of screening tools
 * @param isLoading - Whether data is currently loading
 * @param className - Additional CSS classes
 */
const AssessmentSection: React.FC<AssessmentSectionProps> = ({
  calculators = [],
  riskAssessments = [],
  screeningTools = [],
  isLoading = false,
  className = '',
}) => {
  // Get the appropriate icon component based on icon name in string format
  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'Scale':
        return Scale;
      case 'Flame':
        return Flame;
      case 'Heart':
        return Heart;
      case 'Award':
        return Award;
      default:
        return Calculator;
    }
  };
  
  // Calculate time since last used/completed
  const getTimeAgo = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
      
      if (diffInDays === 0) return 'Today';
      if (diffInDays === 1) return 'Yesterday';
      if (diffInDays < 7) return `${diffInDays} days ago`;
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
      return `${Math.floor(diffInDays / 30)} months ago`;
    } catch (e) {
      return 'Recently';
    }
  };
  
  // If loading, show skeleton components
  if (isLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        {/* Calculators Section Skeleton */}
        <div>
          <h3 className="text-lg font-semibold mb-3 text-[var(--text-primary)]">Calculators</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SkeletonHealthCard contentHeight={180} />
            <SkeletonHealthCard contentHeight={180} />
            <SkeletonHealthCard contentHeight={180} />
          </div>
        </div>
        
        {/* Risk Assessments Section Skeleton */}
        <div>
          <h3 className="text-lg font-semibold mb-3 text-[var(--text-primary)]">Risk Assessments</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SkeletonHealthCard contentHeight={180} />
            <SkeletonHealthCard contentHeight={180} />
          </div>
        </div>
        
        {/* Screening Tools Section Skeleton */}
        <div>
          <h3 className="text-lg font-semibold mb-3 text-[var(--text-primary)]">Screening Tools</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SkeletonHealthCard contentHeight={180} />
            <SkeletonHealthCard contentHeight={180} />
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Calculators Section */}
      <div>
        <h3 className="text-lg font-semibold mb-3 text-[var(--text-primary)]">Calculators</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {calculators.map((calculator, index) => {
            const IconComponent = getIconComponent(calculator.icon);
            
            return (
              <HealthCard
                key={calculator.id || index}
                title={calculator.name}
                icon={IconComponent}
                className="h-full"
              >
                <p className="text-sm text-[var(--text-secondary)] mb-4">
                  {calculator.description}
                </p>
                
                <div className="mt-auto pt-4 flex items-center justify-between border-t border-[var(--border-subtle)]">
                  <span className="text-xs text-[var(--text-tertiary)]">
                    Last used: {getTimeAgo(calculator.lastUsed)}
                  </span>
                  
                  <button className="px-3 py-1 bg-[var(--brand-primary)] text-white text-sm rounded hover:bg-[var(--brand-primary-hover)]">
                    Use
                  </button>
                </div>
              </HealthCard>
            );
          })}
          
          {calculators.length === 0 && (
            <div className="col-span-3 bg-[var(--background-surface)] rounded-lg p-4 text-center">
              <p className="text-[var(--text-secondary)]">No calculators available.</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Risk Assessments Section */}
      <div>
        <h3 className="text-lg font-semibold mb-3 text-[var(--text-primary)]">Risk Assessments</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {riskAssessments.map((assessment, index) => (
            <HealthCard
              key={assessment.id || index}
              title={assessment.name}
              icon={Award}
              className="h-full"
            >
              <p className="text-sm text-[var(--text-secondary)] mb-3">
                {assessment.description}
              </p>
              
              <div className="bg-[var(--background-inset)] p-3 rounded-lg mb-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-[var(--text-primary)]">Result:</span>
                  <span className="text-sm text-[var(--text-primary)]">{assessment.result}</span>
                </div>
                
                <div className="w-full h-2 bg-[var(--background-surface)] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[var(--brand-primary)]" 
                    style={{ width: `${(assessment.resultScore / assessment.maxScore) * 100}%` }}
                  ></div>
                </div>
                
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-[var(--text-tertiary)]">Score: {assessment.resultScore}</span>
                  <span className="text-xs text-[var(--text-tertiary)]">Max: {assessment.maxScore}</span>
                </div>
              </div>
              
              <div className="mt-auto pt-2 flex items-center justify-between">
                <span className="text-xs text-[var(--text-tertiary)]">
                  Completed: {getTimeAgo(assessment.lastCompleted)}
                </span>
                
                <button className="px-3 py-1 bg-[var(--brand-primary)] text-white text-sm rounded hover:bg-[var(--brand-primary-hover)]">
                  Retake
                </button>
              </div>
            </HealthCard>
          ))}
          
          {riskAssessments.length === 0 && (
            <div className="col-span-2 bg-[var(--background-surface)] rounded-lg p-4 text-center">
              <p className="text-[var(--text-secondary)]">No risk assessments available.</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Screening Tools Section */}
      <div>
        <h3 className="text-lg font-semibold mb-3 text-[var(--text-primary)]">Screening Tools</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {screeningTools.map((tool, index) => (
            <HealthCard
              key={tool.id || index}
              title={tool.name}
              icon={Calculator}
              className="h-full"
            >
              <p className="text-sm text-[var(--text-secondary)] mb-3">
                {tool.description}
              </p>
              
              <div className="bg-[var(--background-inset)] p-3 rounded-lg mb-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-[var(--text-primary)]">Result:</span>
                  <span className="text-sm text-[var(--text-primary)]">{tool.result}</span>
                </div>
                
                <div className="w-full h-2 bg-[var(--background-surface)] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[var(--clinical-warning)]" 
                    style={{ width: `${(tool.resultScore / tool.maxScore) * 100}%` }}
                  ></div>
                </div>
                
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-[var(--text-tertiary)]">Score: {tool.resultScore}</span>
                  <span className="text-xs text-[var(--text-tertiary)]">Max: {tool.maxScore}</span>
                </div>
              </div>
              
              <div className="mt-auto pt-2 flex items-center justify-between">
                <span className="text-xs text-[var(--text-tertiary)]">
                  Completed: {getTimeAgo(tool.lastCompleted)}
                </span>
                
                <button className="px-3 py-1 bg-[var(--brand-primary)] text-white text-sm rounded hover:bg-[var(--brand-primary-hover)]">
                  Retake
                </button>
              </div>
            </HealthCard>
          ))}
          
          {screeningTools.length === 0 && (
            <div className="col-span-2 bg-[var(--background-surface)] rounded-lg p-4 text-center">
              <p className="text-[var(--text-secondary)]">No screening tools available.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentSection; 